import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  Moon,
  Bell,
  Globe,
  Shield,
  Save,
  RefreshCcw,
  CreditCard,
  Plus,
  Trash2,
  Image,
  Layout,
  Type,
  AlertCircle,
  CheckCircle2,
  MessageCircle
} from 'lucide-react';
import apiClient from '../api/client';
import { ConfigDto, ThemeSlideDto } from '../types/dtos';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Config() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<ConfigDto | null>(null);
  const [initialConfig, setInitialConfig] = useState<ConfigDto | null>(null);
  const [slides, setSlides] = useState<ThemeSlideDto[]>([]);
  const [initialSlides, setInitialSlides] = useState<ThemeSlideDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [branding, setBranding] = useState({
    primaryColor: '#153871',
    secondaryColor: '#1b4382',
    accentColor: '#009688',
    logoUrl: '/logo.png',
    faviconUrl: '/favicon.ico',
    portalName: 'Xtrem Insure'
  });
  const [initialBranding, setInitialBranding] = useState(branding);

  useEffect(() => {
    fetchData();
  }, []);

  // Apply branding colors to CSS variables live as they change
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primaryColor);
    root.style.setProperty('--brand-secondary', branding.secondaryColor);
    root.style.setProperty('--brand-accent', branding.accentColor);
  }, [branding.primaryColor, branding.secondaryColor, branding.accentColor]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configRes, slidesRes, brandingRes] = await Promise.all([
        apiClient.get<ConfigDto>('/admin/config'),
        apiClient.get<ThemeSlideDto[]>('/admin/config/slides'),
        apiClient.get('/admin/companies/branding')
      ]);
      setConfig(configRes.data);
      setInitialConfig(configRes.data);
      setSlides(slidesRes.data);
      setInitialSlides(slidesRes.data);
      if (brandingRes.data) {
        // Normalize: admin API returns PascalCase (no camelCase policy set)
        const d = brandingRes.data;
        const bData = {
          primaryColor:   d.primaryColor   ?? d.PrimaryColor   ?? '#153871',
          secondaryColor: d.secondaryColor ?? d.SecondaryColor ?? '#1b4382',
          accentColor:    d.accentColor    ?? d.AccentColor    ?? '#009688',
          logoUrl:        d.logoUrl        ?? d.LogoUrl        ?? '/logo.png',
          faviconUrl:     d.faviconUrl     ?? d.FaviconUrl     ?? '/favicon.ico',
          portalName:     d.portalName     ?? d.PortalName     ?? 'Xtrem Insure'
        };
        setBranding(bData);
        setInitialBranding(bData);
      }
    } catch (err: any) {
      console.error('[Config] Failed to load data:', err);
      setError(err.response?.data?.message || 'Unable to load configuration data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    let configSaved = false;
    let brandingSaved = false;

    // Save Config
    try {
      await apiClient.put('/admin/config', config);
      configSaved = true;
      setInitialConfig(config);
      setInitialSlides(slides);
    } catch (err: any) {
      console.error('[Config] Failed to save config:', err.response?.status, err.response?.data);
      setError(err.response?.data?.message || 'Failed to save configuration.');
    }

    // Save Branding (independent of config save)
    try {
      console.log('[Config] Saving branding:', branding);
      const res = await apiClient.put('/admin/companies/branding', branding);
      console.log('[Config] Branding save response:', res.data);
      brandingSaved = true;
      setInitialBranding(branding);

      // Notify customer portal tabs to re-fetch
      try {
        const bc = new BroadcastChannel('branding_update');
        bc.postMessage({ type: 'BRANDING_UPDATED', branding });
        bc.close();
      } catch (_) {}
    } catch (err: any) {
      console.error('[Config] Failed to save branding:', err.response?.status, err.response?.data);
      setError(prev => prev
        ? prev + ' Also failed to save branding.'
        : (err.response?.data?.message || 'Failed to save branding.'));
    }

    if (configSaved || brandingSaved) {
      setSuccess(
        configSaved && brandingSaved
          ? 'Configuration and branding updated successfully!'
          : brandingSaved
          ? 'Branding updated successfully!'
          : 'Configuration updated successfully!'
      );
      setTimeout(() => setSuccess(null), 3000);
    }
    setSaving(false);
  };

  const addSlide = () => {
    const newSlide: any = {
      id: '', // Temp ID
      heroTitle: 'Hero Main Message',
      heroBrand: 'Brand Name',
      heroDescription: 'Description line...',
      imageUrl: '../img/hero-family.jpg',
      order: slides.length,
      isActive: true
    };
    setSlides([...slides, newSlide]);
  };

  const removeSlide = async (id: string, index: number) => {
    if (!id) {
      setSlides(slides.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm('Are you sure you want to delete this slide?')) return;

    try {
      await apiClient.delete(`/admin/config/slides/${id}`);
      setSlides(slides.filter(s => s.id !== id));
      setSuccess('Slide deleted successfully');
    } catch (err) {
      setError('Failed to delete slide');
    }
  };

  const saveSlide = async (slide: ThemeSlideDto, index: number) => {
    try {
      if (!slide.id) {
        const res = await apiClient.post<ThemeSlideDto>('/admin/config/slides', slide);
        const newSlides = [...slides];
        newSlides[index] = res.data;
        setSlides(newSlides);
      } else {
        await apiClient.put(`/admin/config/slides/${slide.id}`, slide);
      }
      setSuccess('Slide saved successfully');
    } catch (err) {
      setError('Failed to save slide');
    }
  };

  const updateSlideField = (index: number, field: keyof ThemeSlideDto, value: any) => {
    const newSlides = [...slides];
    newSlides[index] = { ...newSlides[index], [field]: value };
    setSlides(newSlides);
  };

  const handleFileChange = async (index: number, file: File) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiClient.post<{ url: string }>('/admin/config/slides/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateSlideField(index, 'imageUrl', res.data.url);
      setSuccess('Image uploaded successfully');
    } catch (err) {
      console.error('[Config] Upload failed:', err);
      setError('Failed to upload image. Please try again.');
    }
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(initialConfig) || JSON.stringify(slides) !== JSON.stringify(initialSlides) || JSON.stringify(branding) !== JSON.stringify(initialBranding);

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-black text-[#1E293B] dark:text-slate-100 flex items-center gap-3">
                <Settings className="w-9 h-9 text-brand-primary" /> Configuration Hub
              </h1>
              <p className="text-[#64748B] dark:text-slate-400 mt-1 text-lg">Manage global portal configurations and preferences</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={fetchData}
                className="p-2.5 text-[#64748B] dark:text-slate-400 hover:bg-[#ffffff] hover:text-brand-primary dark:hover:bg-slate-800 dark:hover:text-brand-primary rounded-xl border border-transparent hover:border-[#E2E8F0] dark:hover:border-slate-800 transition-all bg-transparent"
                title="Refresh Configuration"
              >
                <RefreshCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={handleSave}
                disabled={(!hasChanges && slides.length === initialSlides.length) || saving}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold transition-all shadow-sm
                  ${(!hasChanges && slides.length === initialSlides.length) || saving
                    ? 'bg-gray-200 dark:bg-slate-800 text-gray-500 dark:text-slate-500 cursor-not-allowed'
                    : 'bg-brand-primary text-white hover:bg-[#1e40af] active:scale-95 shadow-lg shadow-blue-900/10'}`}
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 text-red-700 dark:text-red-400 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
              <CheckCircle2 className="w-5 h-5" />
              {success}
            </div>
          )}

          {/* Config Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* PayChangu Integration */}
            <div className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#F1F5F9] dark:border-slate-800 md:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-2xl">
                    <CreditCard className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1E293B] dark:text-slate-200">PayChangu Integration</h2>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 px-4">
                  <span className="text-sm font-semibold text-gray-600 dark:text-slate-350">Sandbox Mode</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={config?.payChanguIsSandbox}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, payChanguIsSandbox: e.target.checked } : null)}
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block font-mono">PUBLIC KEY</label>
                    <input
                      type="text"
                      value={config?.payChanguPublicKey || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, payChanguPublicKey: e.target.value } : null)}
                      placeholder="pk_test_..."
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block font-mono">SECRET KEY</label>
                    <input
                      type="password"
                      value={config?.payChanguSecretKey || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, payChanguSecretKey: e.target.value } : null)}
                      placeholder="sec_test_..."
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block font-mono">WEBHOOK SECRET</label>
                    <input
                      type="text"
                      value={config?.payChanguWebhookSecret || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, payChanguWebhookSecret: e.target.value } : null)}
                      placeholder="whsec_..."
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block font-mono">BASE API URL</label>
                    <input
                      type="text"
                      value={config?.payChanguBaseUrl || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, payChanguBaseUrl: e.target.value } : null)}
                      placeholder="https://api.paychangu.com"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block font-mono">WEBHOOK / CALLBACK URL</label>
                    <input
                      type="text"
                      value={config?.payChanguRedirectUrl || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, payChanguRedirectUrl: e.target.value } : null)}
                      placeholder="https://api.Xtrem-customer.co.mw/api/v1/customer/payments/webhook"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block font-mono">PAYMENT RETURN URL</label>
                    <input
                      type="text"
                      value={config?.paymentReturnUrl || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, paymentReturnUrl: e.target.value } : null)}
                      placeholder="https://customer-portal.Xtrem.co.mw/payments"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block font-mono">PAYMENT CANCEL URL</label>
                    <input
                      type="text"
                      value={config?.paymentCancelUrl || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, paymentCancelUrl: e.target.value } : null)}
                      placeholder="https://customer-portal.Xtrem.co.mw/payments"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                <div className="flex gap-3 mb-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <div className="text-xs text-blue-900 dark:text-blue-400 leading-relaxed font-semibold">
                    Understanding Payment URLs:
                  </div>
                </div>
                <div className="ml-8 space-y-2 text-xs text-blue-800 dark:text-blue-400/80">
                  <div>
                    <strong className="font-mono">WEBHOOK / CALLBACK URL:</strong> Backend API endpoint where PayChangu sends POST notifications.
                  </div>
                  <div>
                    <strong className="font-mono">PAYMENT RETURN URL:</strong> Frontend page where users land after completing payment.
                  </div>
                  <div>
                    <strong className="font-mono">PAYMENT CANCEL URL:</strong> Frontend page where users land if they cancel payment.
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Slider Management */}
            <div className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#F1F5F9] dark:border-slate-800 md:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-2xl">
                    <Layout className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1E293B] dark:text-slate-200">Keycloak Hero Slider</h2>
                </div>
                <button
                  onClick={addSlide}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Add New Slide
                </button>
              </div>

              <div className="space-y-6">
                {slides.length === 0 && (
                  <div className="text-center py-12 bg-gray-50 dark:bg-slate-900 rounded-2xl border-2 border-dashed border-gray-200 dark:border-slate-800">
                    <p className="text-gray-500 dark:text-slate-400">No slides configured. Add a slide to manage Keycloak theme content.</p>
                  </div>
                )}
                {slides.map((slide, index) => (
                  <div key={slide.id || `new-${index}`} className="p-6 bg-[#F8FAFC] dark:bg-slate-900/60 rounded-2xl border border-[#E2E8F0] dark:border-slate-800 relative group">
                    <div className="absolute top-4 right-4 flex items-center gap-2">
                      <button
                        onClick={() => saveSlide(slide, index)}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg transition-colors"
                        title="Save this slide"
                      >
                        <Save className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => removeSlide(slide.id, index)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                        title="Delete slide"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider mb-1.5 block">Hero Card Title</label>
                          <input
                            type="text"
                            value={slide.heroTitle}
                            onChange={(e) => updateSlideField(index, 'heroTitle', e.target.value)}
                            className="w-full bg-white border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl p-2.5 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                            placeholder="Peace of mind guaranteed..."
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider mb-1.5 block">Hero Brand / Category</label>
                          <input
                            type="text"
                            value={slide.heroBrand}
                            onChange={(e) => updateSlideField(index, 'heroBrand', e.target.value)}
                            className="w-full bg-white border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl p-2.5 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                            placeholder="Xtrem General"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider mb-1.5 block">Hero Description (HTML allowed)</label>
                          <textarea
                            value={slide.heroDescription}
                            onChange={(e) => updateSlideField(index, 'heroDescription', e.target.value)}
                            className="w-full bg-white border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl p-2.5 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                            placeholder="Motor, Personal, Business..."
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider mb-1.5 block">Hero Image Path / URL</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={slide.imageUrl}
                              onChange={(e) => updateSlideField(index, 'imageUrl', e.target.value)}
                              className="flex-1 bg-white border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl p-2.5 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all font-mono"
                              placeholder="../img/hero-family.jpg"
                            />
                            <input
                              type="file"
                              id={`slide-img-${index}`}
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => e.target.files && handleFileChange(index, e.target.files[0])}
                            />
                            <button
                              type="button"
                              onClick={() => document.getElementById(`slide-img-${index}`)?.click()}
                              className="p-2.5 bg-[#ffffff] hover:bg-[#ffffff] dark:bg-slate-800 dark:hover:bg-slate-700 border border-[#E2E8F0] dark:border-slate-800 rounded-xl transition-colors"
                              title="Select Image"
                            >
                              <Image className="w-5 h-5 text-[#64748B] dark:text-slate-400" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-[#64748B] dark:text-slate-400">Active Slide</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={slide.isActive}
                                onChange={(e) => updateSlideField(index, 'isActive', e.target.checked)}
                              />
                              <div className="w-11 h-6 bg-gray-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                            </label>
                          </div>
                          <div>
                            <input
                              type="number"
                              value={slide.order}
                              onChange={(e) => updateSlideField(index, 'order', parseInt(e.target.value) || 0)}
                              className="w-16 bg-white border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl p-2 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none text-center"
                              title="Display Order"
                            />
                          </div>
                        </div>

                        <div className="aspect-video bg-gray-200 dark:bg-slate-800 rounded-xl overflow-hidden border border-[#E2E8F0] dark:border-slate-700">
                          {slide.imageUrl ? (
                            <div
                              className="w-full h-full bg-cover bg-center flex items-end p-2"
                              style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.5)), url(${slide.imageUrl})` }}
                            >
                              <div className="text-[10px] text-white font-bold truncate opacity-80">{slide.heroBrand}</div>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Image className="w-8 h-8 opacity-20" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* White-Label Portal Branding */}
            <div className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#F1F5F9] md:col-span-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-2xl">
                  <Globe className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#1E293B] dark:text-slate-200">White-Label Portal Branding</h2>
                  <p className="text-xs text-[#64748B] dark:text-slate-400 mt-0.5">Customize display names, company logos, and color palettes applied to the portals.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Customization Inputs */}
                <div className="lg:col-span-2 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block">Portal Display Name</label>
                      <input
                        type="text"
                        value={branding.portalName}
                        onChange={(e) => setBranding(prev => ({ ...prev, portalName: e.target.value }))}
                        placeholder="Xtrem Insure"
                        className="w-full bg-gray-50 dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-800 text-[#1E293B] text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block p-3 transition-colors outline-none font-medium"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block">Logo URL / Path</label>
                      <input
                        type="text"
                        value={branding.logoUrl}
                        onChange={(e) => setBranding(prev => ({ ...prev, logoUrl: e.target.value }))}
                        placeholder="/logo.png"
                        className="w-full bg-gray-50 dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-800 text-[#1E293B] text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block p-3 transition-colors outline-none font-mono"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-semibold text-[#475569] dark:text-slate-300 mb-2 block">Favicon URL / Path</label>
                      <input
                        type="text"
                        value={branding.faviconUrl}
                        onChange={(e) => setBranding(prev => ({ ...prev, faviconUrl: e.target.value }))}
                        placeholder="/favicon.ico"
                        className="w-full bg-gray-50 dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-800 text-[#1E293B] text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block p-3 transition-colors outline-none font-mono"
                      />
                    </div>
                  </div>

                  <hr className="border-[#F1F5F9] dark:border-slate-800 my-4" />

                  <div>
                    <h3 className="text-sm font-bold text-[#334155] dark:text-slate-200 mb-4">Brand Color Palette</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Primary Color */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider block">Primary Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={branding.primaryColor}
                            onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
                            className="w-12 h-11 p-1 bg-[#ffffff] hover:bg-[#ffffff] border border-[#E2E8F0] dark:border-slate-700 rounded-xl cursor-pointer shrink-0"
                          />
                          <input
                            type="text"
                            value={branding.primaryColor}
                            onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
                            className="flex-1 bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 p-2.5 transition-colors outline-none font-mono"
                            maxLength={7}
                          />
                        </div>
                      </div>

                      {/* Secondary Color */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider block">Secondary Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={branding.secondaryColor}
                            onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e.target.value }))}
                            className="w-12 h-11 p-1 bg-[#ffffff] hover:bg-[#ffffff] border border-[#E2E8F0] dark:border-slate-700 rounded-xl cursor-pointer shrink-0"
                          />
                          <input
                            type="text"
                            value={branding.secondaryColor}
                            onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e.target.value }))}
                            className="flex-1 bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 p-2.5 transition-colors outline-none font-mono"
                            maxLength={7}
                          />
                        </div>
                      </div>

                      {/* Accent Color */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider block">Accent Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={branding.accentColor}
                            onChange={(e) => setBranding(prev => ({ ...prev, accentColor: e.target.value }))}
                            className="w-12 h-11 p-1 bg-[#ffffff] hover:bg-[#ffffff] border border-[#E2E8F0] dark:border-slate-700 rounded-xl cursor-pointer shrink-0"
                          />
                          <input
                            type="text"
                            value={branding.accentColor}
                            onChange={(e) => setBranding(prev => ({ ...prev, accentColor: e.target.value }))}
                            className="flex-1 bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 p-2.5 transition-colors outline-none font-mono"
                            maxLength={7}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Theme Preview Card */}
                <div className="bg-[#F8FAFC] dark:bg-slate-900/60 border border-[#E2E8F0] dark:border-slate-800 rounded-2xl p-5 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-[#64748B] dark:text-slate-400 uppercase tracking-wider mb-4 block">Live Visual Preview</h3>
                    
                    {/* Mock Portal UI Header */}
                    <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-slate-800 bg-white">
                      <div 
                        className="p-3 text-white flex items-center justify-between text-xs font-bold transition-all"
                        style={{ backgroundColor: branding.primaryColor }}
                      >
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 bg-white/20 rounded flex items-center justify-center font-bold text-[10px]">
                            {branding.portalName ? branding.portalName.substring(0, 1) : 'X'}
                          </div>
                          <span>{branding.portalName || 'Portal'}</span>
                        </div>
                        <div className="w-4 h-4 rounded-full bg-white/30"></div>
                      </div>
                      
                      {/* Mock Portal UI Body */}
                      <div className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="h-2 w-12 bg-gray-200 dark:bg-slate-700 rounded"></span>
                          <span 
                            className="h-4 px-2 py-0.5 rounded text-[8px] font-bold text-white transition-all animate-pulse"
                            style={{ backgroundColor: branding.accentColor }}
                          >
                            Active
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="h-3 w-28 bg-gray-300 dark:bg-slate-600 rounded"></div>
                          <div className="h-2 w-16 bg-gray-200 dark:bg-slate-700 rounded"></div>
                        </div>
                        <div className="pt-2 border-t border-gray-50 dark:border-slate-800 flex justify-end">
                          <button 
                            type="button"
                            className="px-3 py-1.5 text-[9px] font-bold rounded-lg text-white transition-all cursor-default"
                            style={{ backgroundColor: branding.secondaryColor }}
                          >
                            Action Button
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-[10px] text-gray-500 dark:text-slate-400 mt-4 leading-relaxed bg-white/60 dark:bg-slate-900/30 p-2.5 rounded-xl border border-gray-100 dark:border-slate-850">
                    💡 <strong>Pro Tip:</strong> Click the color squares to open the visual color pickers, or type hex codes directly. Changes will apply immediately across both portals upon saving!
                  </div>
                </div>
              </div>
            </div>

            {/* SMTP Email Configuration */}
            <div className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#F1F5F9] dark:border-slate-800 md:col-span-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-2xl">
                    <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1E293B] dark:text-slate-200">SMTP Email Configuration</h2>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 px-4 self-start sm:self-auto">
                  <span className="text-sm font-semibold text-gray-600 dark:text-slate-350">Enable SSL</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={config?.smtpEnableSsl}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, smtpEnableSsl: e.target.checked } : null)}
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block">SMTP HOST</label>
                    <input
                      type="text"
                      value={config?.smtpHost || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, smtpHost: e.target.value } : null)}
                      placeholder="smtp.gmail.com"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block">SMTP PORT</label>
                    <input
                      type="number"
                      value={config?.smtpPort || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, smtpPort: parseInt(e.target.value) || 587 } : null)}
                      placeholder="587"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block">SMTP USERNAME</label>
                    <input
                      type="text"
                      value={config?.smtpUsername || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, smtpUsername: e.target.value } : null)}
                      placeholder="your-email@gmail.com"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block">SMTP PASSWORD</label>
                    <input
                      type="password"
                      value={config?.smtpPassword || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, smtpPassword: e.target.value } : null)}
                      placeholder="••••••••••••••••"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block">FROM EMAIL</label>
                    <input
                      type="email"
                      value={config?.smtpFromEmail || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, smtpFromEmail: e.target.value } : null)}
                      placeholder="noreply@Xtrem.co.mw"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block">FROM NAME</label>
                    <input
                      type="text"
                      value={config?.smtpFromName || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, smtpFromName: e.target.value } : null)}
                      placeholder="Xtrem Portal"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp Meta Integration */}
            <div className="bg-white p-6 rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#F1F5F9] dark:border-slate-800 md:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-teal-50 dark:bg-teal-950/30 rounded-2xl">
                    <MessageCircle className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#1E293B] dark:text-slate-200">Meta WhatsApp Cloud Integration</h2>
                    <p className="text-xs text-[#64748B] dark:text-slate-400 mt-0.5">Manage credentials for the self-service WhatsApp Customer Bot</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">WEBHOOK VERIFY TOKEN</label>
                    <input
                      type="text"
                      value={config?.whatsAppVerifyToken || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, whatsAppVerifyToken: e.target.value } : null)}
                      placeholder="e.g. MySecretVerifyToken"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">PHONE NUMBER ID</label>
                    <input
                      type="text"
                      value={config?.whatsAppPhoneNumberId || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, whatsAppPhoneNumberId: e.target.value } : null)}
                      placeholder="e.g. 104938205849302"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">META API VERSION</label>
                    <input
                      type="text"
                      value={config?.whatsAppApiVersion || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, whatsAppApiVersion: e.target.value } : null)}
                      placeholder="v20.0"
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">PERMANENT ACCESS TOKEN</label>
                    <input
                      type="password"
                      value={config?.whatsAppAccessToken || ''}
                      onChange={(e) => setConfig(prev => prev ? { ...prev, whatsAppAccessToken: e.target.value } : null)}
                      placeholder="EAAGz..."
                      className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">WEBHOOK ENDPOINT URL</label>
                  <input
                    type="text"
                    readOnly
                    value="https://api.xtremsystems.com/api/v1/customer/whatsapp/webhook"
                    className="w-full bg-gray-100 dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-800 text-gray-500 dark:text-slate-400 text-sm rounded-xl p-3 outline-none font-mono select-all"
                  />
                  <p className="text-[10px] text-[#64748B] dark:text-slate-400 mt-1.5">Copy and paste this URL into your Meta App dashboard under WhatsApp Configuration webhooks.</p>
                </div>

                <div className="md:col-span-2">
                  <hr className="border-[#F1F5F9] dark:border-slate-800 my-4" />
                  <h3 className="text-md font-bold text-[#1E293B] dark:text-slate-200 mb-2">Gemini AI Fallback Configuration</h3>
                  <p className="text-xs text-[#64748B] dark:text-slate-400 mb-4">Enable automated AI conversations for WhatsApp queries that do not match existing FAQs.</p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">GEMINI API KEY</label>
                  <input
                    type="password"
                    value={config?.geminiApiKey || ''}
                    onChange={(e) => setConfig(prev => prev ? { ...prev, geminiApiKey: e.target.value } : null)}
                    placeholder="AIzaSy..."
                    className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">GEMINI MODEL</label>
                  <input
                    type="text"
                    value={config?.geminiModel || ''}
                    onChange={(e) => setConfig(prev => prev ? { ...prev, geminiModel: e.target.value } : null)}
                    placeholder="gemini-1.5-flash"
                    className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none font-mono"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-[#475569] dark:text-slate-350 mb-2 block font-mono">WHATSAPP BOT SYSTEM PROMPT</label>
                  <textarea
                    value={config?.whatsAppSystemPrompt || ''}
                    onChange={(e) => setConfig(prev => prev ? { ...prev, whatsAppSystemPrompt: e.target.value } : null)}
                    rows={4}
                    placeholder="You are a helpful customer service AI assistant for Xtrem Insure..."
                    className="w-full bg-gray-50 border border-[#E2E8F0] text-[#1E293B] text-sm rounded-xl focus:ring-brand-primary focus:border-brand-primary block p-3 transition-colors outline-none"
                  />
                  <p className="text-[10px] text-[#64748B] dark:text-slate-400 mt-1.5">This prompt controls the Gemini AI's personality. Instruct it to reply exactly with <code>[TAKEOVER]</code> when the customer asks for a human agent.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
}
